import _ from 'lodash'
import ApiClient from './ApiClient'
const apiClient = new ApiClient()

export const getStateListPromise = () => {
  return apiClient.get('/state/all').then((resp) => Promise.resolve(resp?.data))
}

// Supported clinicaltables NPI registries:
//  - 'npi_org' (organizations/practices — the legacy default; name.first/name.last come back empty)
//  - 'npi_idv' (individual providers — returns real first/last names)
// The MCA PCP picker passes 'npi_idv' since a PCP is a person (CARE-106). Same df/column
// order for both, so the result index mapping is unchanged.
const SUPPORTED_NPI_REGISTRIES = ['npi_org', 'npi_idv']

// Defaults to the legacy 'npi_org' and only overrides when a supported value is passed —
// `registry` is interpolated into the URL path, so an unexpected value must never reach it.
export const fetchPhysicianPromise = (searchText, registry) => {
  const npiRegistry = SUPPORTED_NPI_REGISTRIES.includes(registry)
    ? registry
    : 'npi_org'
  return fetch(
    `https://clinicaltables.nlm.nih.gov/api/${npiRegistry}/v3/search?df=NPI,name.first,name.last,name.full,provider_type,addr_practice.line1,addr_practice.line2,addr_practice.city,addr_practice.state,addr_practice.zip,addr_practice.country,addr_practice.full,addr_practice.phone,addr_practice.fax&terms=${searchText}`,
    {
      method: 'GET',
    }
  )
    .then((response) => response.json())
    .then((data) => {
      return Promise.resolve(data)
    })
    .catch((error) => console.log(error))
}

export const getMetadataOptionsPromise = () => {
  return apiClient
    .get('/metadata/options')
    .then((resp) => Promise.resolve(resp?.data))
}

export const uploadSelectionPromise = ({
  optionType,
  selectedOptionId,
  otherInputText,
}) => {
  return apiClient
    .post('/user/upload-selection', {
      body: {
        optionType,
        selectedOptionId,
        otherInputText,
      },
    })
    .then((resp) => Promise.resolve(resp))
}

export const addUserPromise = ({
  firstName,
  lastName,
  preferredFirstName,
  email,
  countryCode,
  phoneNumber,
  stateId,
  healthieId,
  utmSource,
  utmCampaign,
  utmContent,
}) => {
  const newBody = {
    firstName,
    lastName,
    email,
    countryCode,
    phoneNumber,
    stateId,
    healthieId,
  }
  if (!_.isEmpty(preferredFirstName)) {
    newBody.preferredFirstName = preferredFirstName
  }
  const utmParams = {}
  if (!_.isEmpty(utmSource)) {
    utmParams.source = String(utmSource)
  }
  if (!_.isEmpty(utmCampaign)) {
    utmParams.campaign = String(utmCampaign)
  }
  if (!_.isEmpty(utmContent)) {
    utmParams.content = String(utmContent)
  }

  if (!_.isEmpty(utmParams)) {
    newBody.utm = utmParams
  }
  return apiClient
    .post('/user', {
      body: newBody,
    })
    .then((resp) => Promise.resolve(resp))
}

export const providerSupportPromise = ({ stateId, insuranceProviderId }) => {
  return apiClient
    .post('/metadata/check/provider-support', {
      body: {
        stateId,
        insuranceProviderId,
      },
    })
    .then((resp) => Promise.resolve(resp?.data || resp))
}

export const checkUserExistsPromise = (email, stateId) => {
  return apiClient
    .post('/check/exists', {
      body: {
        email,
        stateId,
      },
    })
    .then((resp) => Promise.resolve(resp?.data || resp))
}

export function payStripePromise({
  userId,
  paymentIntentId,
  paymentMethodId,
  productId,
}) {
  return apiClient
    .post('/stripe/purchase', {
      body: _.omitBy(
        {
          userId,
          paymentIntentId,
          paymentMethodId,
          productId,
        },
        _.isNil
      ),
    })
    .then((resp) => {
      return Promise.resolve(resp)
    })
    .catch((e) => {
      return Promise.reject(e)
    })
}

export function getProductsPromise() {
  return apiClient
    .get('/products')
    .then((resp) => {
      return Promise.resolve(resp)
    })
    .catch((e) => {
      return Promise.reject(e)
    })
}

export function addStripeInfoPromise({ paymentMethodId }) {
  return apiClient
    .post('/stripe/attach/payment-method', {
      body: { paymentMethodId },
    })
    .then((resp) => {
      return Promise.resolve(resp)
    })
    .catch((e) => {
      return Promise.reject(e)
    })
}

export const updateUserPromise = (body, authContext) => {
  return apiClient
    .put('/user', {
      body,
    })
    .then((resp) => {
      if (authContext) {
        authContext.updateUserProfile(resp?.data)
      }
      return Promise.resolve(resp?.data || resp)
    })
}

export const updateUserTagPromise = (body) => {
  return apiClient
    .post('/user/tags', {
      body,
    })
    .then((resp) => Promise.resolve(resp))
}

export const createReferringPhysicianPromise = (body) => {
  return apiClient
    .post('/user/referring/physician', {
      body,
    })
    .then((resp) => Promise.resolve(resp))
}

export const autoLoginPromise = (body) => {
  return apiClient
    .post('/user/auto-login', {
      body,
    })
    .then((resp) => Promise.resolve(resp))
}

export const forwardHealthiePromise = ({
  query,
  variables,
  operationName,
} = {}) => {
  return apiClient
    .post('/metadata/forward/healthie', {
      body: { query, variables, operationName },
    })
    .then((resp) => Promise.resolve(resp?.data || resp))
}

export function getUserProfilePromise() {
  return apiClient.get('/user/profile').then((resp) => {
    return Promise.resolve(resp)
  })
}

export const emailMagicLinkPromise = ({ email, stateId } = {}) => {
  return apiClient
    .post('/user/send/magic-link', {
      body: { email, stateId },
    })
    .then((resp) => Promise.resolve(resp))
}

export const verifyMagicLinkByDigitCodePromise = ({
  email,
  digitCode,
  stateId,
} = {}) => {
  return apiClient
    .post('/user/verify/magic-link-code', {
      body: { email, digitCode, stateId },
    })
    .then((resp) => Promise.resolve(resp))
}

export const uploadUnqualifiedPromise = ({
  email,
  stateId,
  selectOptionArray,
}) => {
  return apiClient
    .post('/user/upload-unqualified', {
      body: {
        email,
        stateId,
        selectOptionArray,
      },
    })
    .then((resp) => Promise.resolve(resp))
}

export const verifyMagicLinkPromise = (code) => {
  return apiClient
    .post('/user/verify/magic-link', {
      body: { code },
    })
    .then((resp) => Promise.resolve(resp))
}

export const joinSkuProgramsPromise = ({
  userId,
  sku,
  subCategoryId,
  programIds,
  isMca,
}) => {
  return apiClient
    .post('/user/join/sku-programs', {
      body: { userId, sku, subCategoryId, programIds, isMca },
    })
    .then((resp) => Promise.resolve(resp))
}

export function getMskThemesPromise({ gender } = {}) {
  return apiClient
    .get('/metadata/msk/themes', {
      params: { gender },
    })
    .then((resp) => {
      return Promise.resolve(resp?.data || resp)
    })
}

export const getSkuThemesPromise = ({ gender, sku } = {}) => {
  return apiClient
    .get('/metadata/sku/themes', {
      params: { gender, sku },
    })
    .then((resp) => {
      return Promise.resolve(resp?.data || resp)
    })
}

export const completeOnboardingPromise = ({
  userId,
  theme,
  themeId,
  programId,
}) => {
  return apiClient
    .post('/user/onboarding/complete', {
      body: { userId, theme, themeId, programId },
    })
    .then((resp) => Promise.resolve(resp))
}

export function getRehabProgramPromise({ userId, programId, subCategoryId }) {
  return apiClient
    .get('/user/rehab/program', {
      params: { userId, programId, subCategoryId },
    })
    .then((resp) => {
      return Promise.resolve(resp?.data || resp)
    })
}

export const getRehabProgramV2Promise = ({
  userId,
  programId,
  subCategoryIds,
}) => {
  return apiClient
    .get('/user/rehab/program-v2', {
      params: { userId, programId, subCategoryIds },
    })
    .then((resp) => {
      return Promise.resolve(resp?.data || resp)
    })
}

export const completeOnboardingV2Promise = ({
  userId,
  themeIds,
  programId,
}) => {
  return apiClient
    .post('/user/onboarding/complete-v2', {
      body: { userId, themeIds, programId },
    })
    .then((resp) => Promise.resolve(resp))
}

export const getSwitchListByNamePromise = (names) => {
  return apiClient
    .post('/util/switches', { body: { name: names } })
    .then((resp) => Promise.resolve(resp && resp.data))
}

export const getThemesByProgramIdPromise = ({ userId, programId }) => {
  return apiClient
    .get('/user/themes/by/program', {
      params: { userId, programId },
    })
    .then((resp) => {
      return Promise.resolve(resp?.data || resp)
    })
}

export const updateUserHealthieInfoPromise = ({
  healthieUserId,
  stateType,
  active,
  stateId,
}) => {
  return apiClient
    .post('/user/healthie/active-status', {
      body: { healthieUserId, stateType, active, stateId },
    })
    .then((resp) => Promise.resolve(resp))
}

export const userAutoLoginPromise = ({ u, e, t, h }) => {
  return apiClient
    .post('/user/bind/fiton-user/by/auto-login', {
      body: { u, e, t, h },
    })
    .then((resp) => Promise.resolve(resp))
}

export const getMcaEnrollmentPromise = () => {
  return apiClient
    .get('/medicare-access/current/enrollment')
    .then((resp) => Promise.resolve(resp))
}

export const submitMBIPromise = ({ u, e, t, h, mbi }) => {
  return apiClient
    .post('/medicare-access/mbi', {
      body: { u, e, t, h, mbi },
    })
    .then((resp) => Promise.resolve(resp))
}

export const screenerAnswersPromise = (body) => {
  return apiClient
    .post('/medicare-access/screener/answers', {
      body,
    })
    .then((resp) => Promise.resolve(resp))
}

export const submitEligibilityPromise = () => {
  return apiClient
    .post('/medicare-access/eligibility/submit', {
      body: {},
    })
    .then((resp) => Promise.resolve(resp))
}

export const getMcaEligibilityStatusPromise = () => {
  return apiClient
    .get('/medicare-access/eligibility/status')
    .then((resp) => Promise.resolve(resp))
}

export const resubmitMbiPromise = ({
  firstName,
  lastName,
  dateOfBirth,
  gender,
  mbi,
}) => {
  return apiClient
    .post('/medicare-access/mbi/resubmit', {
      body: {
        firstName,
        lastName,
        dateOfBirth,
        gender,
        mbi,
      },
    })
    .then((resp) => Promise.resolve(resp))
}

export const identityLaunchPromise = () => {
  return apiClient
    .post('/medicare-access/identity/launch', {
      body: {},
    })
    .then((resp) => Promise.resolve(resp))
}

export const identityCompletePromise = ({
  state,
  verificationId,
  outcome,
  fitonHealthUserId,
  providerToken,
}) => {
  return apiClient
    .post('/medicare-access/identity/complete', {
      body: {
        state,
        verificationId,
        outcome,
        fitonHealthUserId,
        providerToken,
      },
    })
    .then((resp) => Promise.resolve(resp))
}

export const alignTracksPromise = ({
  acceptedTracks,
  participationVoluntary,
  oneParticipantPerTrack,
  mayDisenrollAfter90Days,
  costSharingAcknowledged,
}) => {
  return apiClient
    .post('/medicare-access/align', {
      body: {
        acceptedTracks,
        consentAttestations: {
          participationVoluntary,
          oneParticipantPerTrack,
          mayDisenrollAfter90Days,
          costSharingAcknowledged,
        },
      },
    })
    .then((resp) => Promise.resolve(resp))
}

export const alignRetrySideEffectsPromise = () => {
  return apiClient
    .post('/medicare-access/align/retry-side-effects', {
      body: {},
    })
    .then((resp) => Promise.resolve(resp))
}

export const mcaPcpPromise = ({
  providerName,
  firstName,
  lastName,
  practice,
  npi,
  phone,
  fax,
  location,
  acceptsInsurance,
}) => {
  return apiClient
    .post('/medicare-access/pcp', {
      body: {
        providerName,
        firstName,
        lastName,
        practice,
        npi,
        phone,
        fax,
        acceptsInsurance,
        location: {
          line1: location.line1,
          line2: location.line2,
          city: location.city,
          state: location.state,
          zip: location.zip,
          country: location.country,
        },
      },
    })
    .then((resp) => Promise.resolve(resp))
}

export const mcaCompletePromise = () => {
  return apiClient
    .post('/medicare-access/complete', {
      body: {},
    })
    .then((resp) => Promise.resolve(resp))
}

export const mcaScreenerPromise = () => {
  return apiClient
    .get('/medicare-access/screener')
    .then((resp) => Promise.resolve(resp))
}

export const featureFlagPromise = ({ name } = {}) => {
  return apiClient
    .post('/util/switches', {
      body: { name },
    })
    .then((resp) => Promise.resolve(resp))
}

export const submitPainActivityAssessmentPromise = ({ items }) => {
  return apiClient
    .post('/assessment/pain-activity', {
      body: { items },
    })
    .then((resp) => Promise.resolve(resp))
}
